import isNil from 'lodash/isNil'

import { createSelector } from '@app/utils/createSelector'
import { createSortHandler } from '@app/utils/createSortHandler'
import moment from '@app/utils/moment'
import { normalize } from '@app/utils/normalizer'

import type { ApiYandexMapsReview, ApiYandexMapsReviewsSummary } from '@app/packages/api/v2/types/entities'
import type { ReviewsPageMeta, YandexMapsReviewRequest } from '@app/packages/api/v2/types/params'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { routingDateSelector, routingUUIDSelector } from './routing'

const summarySlice = createReduxSlice<SliceState<ApiYandexMapsReviewsSummary>>('yandex_maps_reviews_summary')

const reviewsSlice = createReduxSlice<SliceState<Record<string, ApiYandexMapsReview>>>('yandex_maps_reviews')

const metaSlice = createReduxSlice<Meta>('yandex_maps_reviews_meta')

const setReviewsPageMetaSlice = (meta: ReviewsPageMeta) => metaSlice.update(state => ({ ...state, ...meta, loading: false }))

const setReviewsLoadingSlice = (loading: boolean) => metaSlice.update(state => ({ ...state, loading }))

export const yandexMapsReviewsLoadedSelector = createSelector([metaSlice.selector], meta => {
  return !isNil(meta?.current_page) && !isNil(meta.total_pages) && meta.current_page >= meta.total_pages
})

export const yandexMapsReviewsSummaryResultSelector = createSelector([summarySlice.selector, routingDateSelector], (sliceState, date) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: summarySlice,
      date: { date, expiresInMinutes: 30 },
      load: async dispatch => {
        const [{ getYandexMapsReviewsSummary }, { callV2Api }] = await Promise.all([
          import('@app/packages/api/v2/actions/yandex_maps_reviews'),
          import('@app/store/actions/api'),
        ])
        const resp = await dispatch(callV2Api(getYandexMapsReviewsSummary()))
        return resp.data
      },
    })
  )
)

const yandexMapsReviewsModelsResultSelector = createSelector([reviewsSlice.selector, routingUUIDSelector], (sliceState, uuid) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: reviewsSlice,
      hash: uuid,
      load: async dispatch => {
        const [{ getYandexMapsReviewsByQuery }, { callV2Api }] = await Promise.all([
          import('@app/packages/api/v2/actions/yandex_maps_reviews'),
          import('@app/store/actions/api'),
        ])
        const resp = await dispatch(callV2Api(getYandexMapsReviewsByQuery({ ...BASIC_REQUEST, page: 1 })))
        dispatch(setReviewsPageMetaSlice(resp.meta))
        return normalize(resp).yandex_maps_reviews ?? {}
      },
    })
  )
)

export const yandexMapsReviewsListResultSelector = mapThunkSelector(yandexMapsReviewsModelsResultSelector, models =>
  Object.values(models).sort(createSortHandler(r => [moment(r.attributes.created_at).unix()]))
)

export const fetchNextYandexMapsReviews = () =>
  createThunk(async (dispatch, getState) => {
    const [{ getYandexMapsReviewsByQuery }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/yandex_maps_reviews'),
      import('@app/store/actions/api'),
    ])
    const meta = metaSlice.selector(getState())
    if (meta?.loading) return
    dispatch(setReviewsLoadingSlice(true))
    try {
      const resp = await dispatch(callV2Api(getYandexMapsReviewsByQuery({ ...BASIC_REQUEST, page: (meta?.current_page ?? 0) + 1 })))
      dispatch(reviewsSlice.update(state => (state ? { ...state, data: { ...state.data, ...normalize(resp).yandex_maps_reviews } } : state)))
      dispatch(setReviewsPageMetaSlice(resp.meta))
    } catch (e) {
      dispatch(setReviewsLoadingSlice(false))
      throw e
    }
  })

type Meta = { total?: number; total_pages?: number; current_page?: number; loading?: boolean }

const BASIC_REQUEST: YandexMapsReviewRequest = {
  sort: '-created_at',
  per_page: 10,
}
